import {CleanHtml, emptyCleanHtml, iSolemnlySwearThisHtmlIsClean} from '@frontapp/editor-html';
import React, {RefObject} from 'react';
import styled from 'styled-components';

import {TimeFormatsEnum} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Localizer} from '../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {CompanyModel} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {PaletteColorsEnum} from '../../../../libs/shared/core/src/models/api/paletteModel';
import {TeammateModel} from '../../../../libs/shared/core/src/models/api/teammateModel';
import {CalendarsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {CheckBox} from '../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {TextBox} from '../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {OkCancelDialog} from '../../../../libs/shared/ui/src/components/dialogs/okCancelDialog';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {textSelection} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CalendarEventTimeFormatter} from '../../../../srcCommon/formatters/dateTime/calendarEventTimeFormatter';
import {StartEndData} from '../../../../srcCommon/helpers/calendarHelpers';
import {addOrganizerToVisualParticipants} from '../../../helpers/models/calendarEventHelpers';
import {
  CalendarEventVersionModel,
  CalendarEventVersionReminder,
  CalendarEventVersionShowAsTypesEnum,
  CalendarEventVersionVisibilityTypesEnum,
} from '../../../models/api/calendarEventVersionModel';
import {CalendarModel, CalendarProviderTypesEnum} from '../../../models/api/calendarModel';
import {CalendarVersionAccessRolesEnum, CalendarVersionModel} from '../../../models/api/calendarVersionModel';
import {ConferenceModel} from '../../../models/api/conferenceModel';
import {RecurrenceModel} from '../../../models/api/recurrenceModel';
import {SchedulingLinkModel} from '../../../models/api/schedulingLinkModel';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarEventSaveData} from '../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {CalendarEventData} from '../../../store/panels/calendars/calendarsPanelSelectors';
import {
  CalendarEventOverrides,
  ViewingNowCalendar,
} from '../../../store/panels/calendars/calendarsPanelState';
import {HtmlEditorBoxHandle} from '../../common/boxedInputs/htmlEditorBox';
import {HtmlEditorBoxContainer} from '../../common/boxedInputs/htmlEditorBoxContainer';
import {SideCalendarsHeader} from '../header/sideCalendarsHeader';
import {CalendarRemindersEditor} from '../reminders/calendarRemindersEditor';
import {InstantReschedulingLinkDialogContainer} from '../scheduling/instantRescheduling/instantReschedulingLinkDialogContainer';
import {CalendarEventDetailRow} from '../viewer/calendarEventDetailRow';
import {CalendarColorDropdownBox} from './calendarColorDropdownBox';
import {SelectedCalendarData} from './calendarDropdownBox';
import {CalendarDropdownBoxContainer} from './calendarDropdownBoxContainer';
import {OnCalendarEventEditorDoneArgs} from './calendarEventEditorContainer';
import {CalendarEventStartEndEditor} from './calendarEventStartEndEditor';
import {CalendarShowAsDropdownBox} from './calendarShowAsDropdownBox';
import {CalendarVisibilityDropdownBox} from './calendarVisibilityDropdownBox';
import {ConferenceEditor} from './conferenceEditor/conferenceEditor';
import {MeetingRoomsEditor} from './meetingRoomsEditor/meetingRoomsEditor';
import {CalendarEventParticipantsEditor} from './participantsEditor/calendarEventParticipantsEditor';
import {RecurringUpdateDialog} from './recurring/recurringUpdateDialog';

/*
 * Props.
 */

export interface CalendarEventEditorProps {
  panelId: CalendarsPanelId;
  viewingNowCalendars: ReadonlyArray<ViewingNowCalendar>;
  calendarEventData: CalendarEventData;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  instantReschedulingLink: SchedulingLinkModel | undefined;
  isEventOrganizer: boolean;
  timeFormat: TimeFormatsEnum;
  fetchOrCreateInstantReschedulingLinkAsync: FetchOrCreateInstantReschedulingLinkType;
  onBackClick?: () => void;
  onRequestClose?: () => void;
  onSaveClick: (data: CalendarEventSaveData) => void;
  onCalendarEventEditorDone: (args: OnCalendarEventEditorDoneArgs) => void;
  onFocusedEventOverridesChange: ((overrides: CalendarEventOverrides) => void) | undefined;
  onVisualParticipantsChange?: (
    oldVisualParticipants: ReadonlyArray<VisualParticipantModel>,
    newVisualParticipants: ReadonlyArray<VisualParticipantModel>,
  ) => void;
  onViewGuestAvailabilityClick?: ((arg: ReadonlyArray<VisualParticipantModel>) => void) | undefined;
  masterCalendarEventVersion?: CalendarEventVersionModel;
  isInstantReschedulingEnabledPreference: boolean;
  user: TeammateModel;
  company: CompanyModel;
  isCreatingFromOrphanEvent?: boolean;
}

export type FetchOrCreateInstantReschedulingLinkType = (calendarVersionId: number) => Promise<void>;

/*
 * Styles.
 */

const StyledInstantReschedulingWrapper = styled.div`
  padding-left: 10px;
  padding-left: 29px;
  margin-top: -12px;
  display: grid;
  grid-template-rows: auto auto;
`;

const StyledHeaderSettingsDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
`;

const StyledInstantReschedulingTitleDiv = styled.div`
  padding-left: 15px;
  display: grid;
  grid-template-columns: auto 1fr;
`;

const StyledSetAvailableTimesWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  margin-left: 17px;
  padding-top: 5px;
`;

const StyledInstantReschedulingToolTipDiv = styled.div`
  margin-left: 5px;
`;

const StyledDateSectionWrapperDiv = styled.div`
  margin-top: 12px;
`;

const StyledSectionIcon = styled(Icon)`
  margin-top: 10px;
`;

const StyledBoxWrapperDiv = styled.div`
  position: relative;
  min-width: 0px;

  > div > div {
    margin: 0px;
  }
`;

const StyledCalendarSectionWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 8px;

  div {
    min-width: 0px;
    padding: 0;
  }
`;

const StyledMoreOptionsSectionWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 5px;
`;

const StyledHtmlEditorBoxContainer = styled(HtmlEditorBoxContainer)`
  min-height: 70px;
  word-break: break-word;
`;

const StyledCalendarShowAsDropdownBox = styled(CalendarShowAsDropdownBox)`
  width: 135px;
`;

const StyledCalendarVisibilityDropdownBox = styled(CalendarVisibilityDropdownBox)`
  width: 150px;
`;

const StyledTextDiv = styled.div`
  line-height: 21px;
  white-space: pre-wrap;
  min-width: 0px;
  overflow: hidden;
  ${textSelection()}
`;

/*
 * Component.
 */

enum RecurringEventSaveTypesEnum {
  ENTIRE_SERIES = 'entire_series',
  INSTANCE = 'instance',
}

interface SaveOptions {
  hasConfirmedSave?: boolean;
  recurringSaveType?: RecurringEventSaveTypesEnum;
}

export function renderInstantReschedulingDialog(
  props: CalendarEventEditorProps,
  newLinkDialogPanelId: CalendarsPanelId,
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  instantReschedulingLink: SchedulingLinkModel | undefined,
  onNewLinkDialogClose: () => void,
) {
  if (!instantReschedulingLink) {
    return null;
  }

  return (
    <InstantReschedulingLinkDialogContainer
      panelId={newLinkDialogPanelId}
      parentPanelId={props.panelId}
      visualParticipants={nonResourceParticipants}
      schedulingLinkId={instantReschedulingLink.id}
      onDialogClose={onNewLinkDialogClose}
    />
  );
}

export function maybeRenderInstantReschedulingCheckBox(
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  isInstantReschedulingEnabled: boolean,
  isUnsavedDraft: boolean,
  instantReschedulingLink: SchedulingLinkModel | undefined,
  toggleIsInstantReschedulingEnabled: (arg: boolean) => void,
  onNewLinkClick: () => void,
) {
  if (!isUnsavedDraft || !hasAnyParticipantsExcludeGlobalOrganizer(nonResourceParticipants)) {
    return null;
  }

  return (
    <StyledInstantReschedulingWrapper>
      <StyledHeaderSettingsDiv>
        <CheckBox
          isDisabled={!instantReschedulingLink}
          isChecked={instantReschedulingLink && isInstantReschedulingEnabled}
          onChange={toggleIsInstantReschedulingEnabled}
        />
        <StyledInstantReschedulingTitleDiv>
          <Trans id="calendar-event-instant-reschedule-title">Guests can reschedule</Trans>
          <StyledInstantReschedulingToolTipDiv>
            <TooltipCoordinator
              render={() => (
                <Trans id="calendar-instant-reschedule-tooltip">
                  Include a rescheduling link in the meeting description to allow guests to reschedule to
                  another available time.
                </Trans>
              )}
            />
          </StyledInstantReschedulingToolTipDiv>
        </StyledInstantReschedulingTitleDiv>
      </StyledHeaderSettingsDiv>
      <StyledSetAvailableTimesWrapperDiv>
        <Button
          isDisabled={!instantReschedulingLink}
          variant={ButtonVariantsEnum.LINK}
          onClick={onNewLinkClick}
        >
          <Trans id="calendar-instant-reschedule-set-available">Set available times</Trans>
        </Button>
      </StyledSetAvailableTimesWrapperDiv>
    </StyledInstantReschedulingWrapper>
  );
}

export function hasAnyParticipantsExcludeGlobalOrganizer(
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
) {
  if (nonResourceParticipants.length === 0) {
    return false;
  }
  return nonResourceParticipants.filter((p) => p.namespace !== 'global').length > 0;
}

export function getRecurringSaveTypeOverride(hasChangedStartEnd: boolean, hasChangedRecurrence: boolean) {
  if (hasChangedStartEnd) {
    return {recurringSaveType: RecurringEventSaveTypesEnum.INSTANCE};
  }

  if (hasChangedRecurrence) {
    return {recurringSaveType: RecurringEventSaveTypesEnum.ENTIRE_SERIES};
  }

  return {};
}

export function addConferenceToEventDescription(
  eventDescription: CleanHtml | undefined,
  conferenceDescription: CleanHtml,
): CleanHtml {
  const separator = iSolemnlySwearThisHtmlIsClean('<div>──────────</div>');
  const separatedConferenceDescription = iSolemnlySwearThisHtmlIsClean(
    `${separator}${conferenceDescription}${separator}`,
  );

  // If the event description is empty, return the conference description.
  if (!eventDescription) {
    return separatedConferenceDescription;
  }

  // If the event has an existing conference description,
  // replace it with the new one.
  const splitEventDescription = eventDescription.split(separator);
  if (splitEventDescription.length === 3) {
    // splitEventDescription[1] is expected to be the old conference description
    return iSolemnlySwearThisHtmlIsClean(
      `${splitEventDescription[0]}${separatedConferenceDescription}${splitEventDescription[2]}`,
    );
  }

  // If the event has no conference description, append
  // the new conference description to it.
  return iSolemnlySwearThisHtmlIsClean(`${eventDescription}${separatedConferenceDescription}`);
}

export function descriptionFromModelOrEditor(
  calendarEventVersion: CalendarEventVersionModel,
  editor: HtmlEditorBoxHandle | null,
): CleanHtml | undefined {
  if (editor) {
    return editor.isEmpty ? emptyCleanHtml : editor.cleanHtml;
  }

  return calendarEventVersion.description;
}

export function renderHeader(
  props: CalendarEventEditorProps,
  isUnsavedDraft: boolean,
  initialViewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
) {
  const headerTitle = isUnsavedDraft ? (
    <Trans id="calendar-header-newevent">New event</Trans>
  ) : (
    <Trans id="calendar-header-editevent">Edit event</Trans>
  );

  return (
    <SideCalendarsHeader title={headerTitle} onBackClick={props.onBackClick}>
      {maybeRenderCloseButton(props, initialViewingNowCalendars)}
    </SideCalendarsHeader>
  );
}

export function maybeRenderCloseButton(
  props: CalendarEventEditorProps,
  initialViewingNowCalendars: ReadonlyArray<ViewingNowCalendar>,
) {
  if (!props.onRequestClose) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <IconButton
          iconName="close"
          onClick={() =>
            props.onCalendarEventEditorDone({
              viewingNowCalendarsToResetTo: initialViewingNowCalendars,
              finalHandler: props.onRequestClose,
            })
          }
          label={<Trans id="calendar-header-close">Close</Trans>}
          color={theme.greys.shade70}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderSummaryInput(
  localize: Localizer,
  props: CalendarEventEditorProps,
  summary: string,
  setSummary: (arg: string) => void,
  isDisabled: boolean,
) {
  return (
    <StyledBoxWrapperDiv>
      <TextBox
        size={VisualSizesEnum.LARGE}
        placeholder={localize(
          defineMessage({
            id: 'eventeditor-addtitle',
            message: 'Add a title',
          }),
        )}
        value={summary}
        onChange={setSummary}
        onBlur={() => {
          if (props.onFocusedEventOverridesChange) {
            props.onFocusedEventOverridesChange({summary});
          }
        }}
        shouldAutoFocus
        isDisabled={isDisabled}
      />
    </StyledBoxWrapperDiv>
  );
}

export function renderDateSection(
  startEndData: StartEndData,
  setStartEndData: (data: StartEndData) => void,
  timeFormat: TimeFormatsEnum,
  calendarEventVersion: CalendarEventVersionModel,
  instanceStartDate: number | undefined,
  masterRecurrence: Readonly<RecurrenceModel> | undefined,
  recurrence: Readonly<RecurrenceModel> | undefined,
  setRecurrence: (arg: RecurrenceModel | undefined) => void,
  isRecurrenceDisabled: boolean,
  isDisabled: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledDateSectionWrapperDiv>
          <CalendarEventDetailRow
            iconContent={<StyledSectionIcon name="snoozeSmall" color={theme.greys.shade70} />}
            content={
              isDisabled ? (
                <StyledTextDiv>
                  <CalendarEventTimeFormatter
                    timeFormat={timeFormat}
                    calendarEventVersion={calendarEventVersion}
                    instanceStartDate={instanceStartDate}
                    shouldUseMultiline
                  />
                </StyledTextDiv>
              ) : (
                <CalendarEventStartEndEditor
                  timeFormat={timeFormat}
                  startDateTime={startEndData.startDateTime}
                  endDateTime={startEndData.endDateTime}
                  isAllDay={startEndData.isAllDay}
                  masterRecurrence={masterRecurrence}
                  recurrence={recurrence}
                  onRecurrenceChange={isRecurrenceDisabled ? undefined : setRecurrence}
                  onChange={setStartEndData}
                />
              )
            }
          />
        </StyledDateSectionWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}

export function renderParticipantsSection(
  props: CalendarEventEditorProps,
  initialNonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  nonResourceParticipants: ReadonlyArray<VisualParticipantModel>,
  setNonResourceParticipants: (arg: ReadonlyArray<VisualParticipantModel>) => void,
  canInviteParticipants: boolean,
  selectedCalendarData: SelectedCalendarData,
  isUnsavedDraft: boolean,
) {
  // If we have no existing participants and can't invite new ones, nothing to render.
  if (nonResourceParticipants.length === 0 && !canInviteParticipants) {
    return null;
  }

  const {calendarEventData} = props;

  const onVisualParticipantsChange = (newParticipants: ReadonlyArray<VisualParticipantModel>) => {
    if (props.onVisualParticipantsChange) {
      props.onVisualParticipantsChange(nonResourceParticipants, newParticipants);
    }

    // Add the organizer to attendees on adding the first attendee, if it's a primary calendarVersion
    const shouldAddOrganizer =
      props.isEventOrganizer &&
      nonResourceParticipants.every((p) => !p.isAttendee) &&
      selectedCalendarData.calendarVersion.isPrimary;

    // If we add/remove the same participant in an already existing event before saving, we should retain the status/state of that participant.
    const mergedNewParticipants = newParticipants.map((newParticipant) => {
      const maybeInitialParticipant = initialNonResourceParticipants.find(
        (initialParticipant) => initialParticipant.contact.handle === newParticipant.contact.handle,
      );
      return maybeInitialParticipant ?? newParticipant;
    });

    const newVisualParticipants = shouldAddOrganizer
      ? addOrganizerToVisualParticipants(
          newParticipants,
          selectedCalendarData.calendar.organizerAddress,
          true,
        )
      : mergedNewParticipants;

    return setNonResourceParticipants(newVisualParticipants);
  };

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="teammatesSmall" color={theme.greys.shade70} />}
          content={
            <CalendarEventParticipantsEditor
              visualParticipants={nonResourceParticipants}
              onVisualParticipantsChange={canInviteParticipants ? onVisualParticipantsChange : undefined}
              readonlyContactUids={
                props.isEventOrganizer
                  ? undefined
                  : calendarEventData.calendarEvent.participants.map((p) => p.contactUid)
              }
              panelId={props.panelId}
              organizerAddress={calendarEventData.calendar.organizerAddress}
              isUnsavedDraft={isUnsavedDraft}
              onViewGuestAvailabilityClick={props.onViewGuestAvailabilityClick}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderLocationSection(
  localize: Localizer,
  props: CalendarEventEditorProps,
  location: string,
  setLocation: (arg: string) => void,
  isDisabled: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="locationSmall" color={theme.greys.shade70} />}
          content={
            <StyledBoxWrapperDiv>
              <TextBox
                size={VisualSizesEnum.MEDIUM}
                placeholder={localize(
                  defineMessage({
                    id: 'cal-event-editor-add-location',
                    message: 'Add a location',
                  }),
                )}
                value={location}
                onChange={setLocation}
                isDisabled={isDisabled}
              />
            </StyledBoxWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderMeetingRoomsSection(
  props: CalendarEventEditorProps,
  calendarVersion: CalendarVersionModel,
  calendar: CalendarModel,
  resourceParticipants: ReadonlyArray<VisualParticipantModel>,
  setResourceParticipants: (arg: ReadonlyArray<VisualParticipantModel>) => void,
  location: string,
  setLocation: (arg: string) => void,
  canInviteParticipants: boolean,
  startEndData: StartEndData,
) {
  const {participants} = props.calendarEventData.calendarEvent;
  const {channelId} = calendarVersion;

  if (!channelId) {
    return null;
  }

  // If we have no existing rooms and this calendar does not support adding new ones, don't render anything.
  if (resourceParticipants.length === 0 && !canInviteParticipants) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="meetingBuildingSmall" color={theme.greys.shade70} />}
          content={
            <MeetingRoomsEditor
              panelId={props.panelId}
              visualParticipants={resourceParticipants}
              onVisualParticipantsChange={canInviteParticipants ? setResourceParticipants : undefined}
              location={location}
              onLocationChange={props.isEventOrganizer ? setLocation : undefined}
              readonlyContactUids={props.isEventOrganizer ? undefined : participants.map((p) => p.contactUid)}
              startEndData={startEndData}
              channelId={channelId}
              calendar={calendar}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderConferenceSection(
  calendarVersion: CalendarVersionModel,
  calendar: CalendarModel,
  conference: ConferenceModel | undefined,
  onConferenceChange: (arg: ConferenceModel | undefined) => void,
  startEndData: StartEndData,
  summary: string,
  isUnsavedDraft: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="videoSmall" color={theme.greys.shade70} />}
          content={
            <ConferenceEditor
              supportedProviderConferenceTypes={calendar.supportedProviderConferenceTypes}
              conference={conference}
              onConferenceChange={onConferenceChange}
              startEndData={startEndData}
              summary={summary}
              calendarVersionId={calendarVersion.id}
              isUnsavedDraft={isUnsavedDraft}
            />
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderDescriptionSection(
  editorRef: RefObject<HtmlEditorBoxHandle>,
  initialDescription: CleanHtml,
  onChange: () => void,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="notesSmall" color={theme.greys.shade70} />}
          content={
            <StyledBoxWrapperDiv>
              <StyledHtmlEditorBoxContainer
                ref={editorRef}
                initialCleanHtml={initialDescription}
                renderPlaceholder={() => (
                  <Trans id="cal-event-editor-add-description">Add a description</Trans>
                )}
                hasToolbar
                onChange={onChange}
              />
            </StyledBoxWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderCalendarSection(
  selectedCalendarData: SelectedCalendarData,
  onSelectedCalendarDataChange: (data: SelectedCalendarData) => void,
  color: PaletteColorsEnum,
  setColor: (arg: PaletteColorsEnum) => void,
  isUnsavedDraft: boolean,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="calendarSmall" color={theme.greys.shade70} />}
          content={
            <StyledCalendarSectionWrapperDiv>
              <CalendarDropdownBoxContainer
                selectedCalendarVersion={selectedCalendarData.calendarVersion}
                onCalendarDataSelect={(data) => {
                  onSelectedCalendarDataChange(data);
                  setColor(data.calendarVersion.color);
                }}
                isDisabled={!isUnsavedDraft}
              />
              <CalendarColorDropdownBox color={color} onColorSelect={setColor} />
            </StyledCalendarSectionWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderRemindersSection(
  reminders: ReadonlyArray<CalendarEventVersionReminder>,
  setReminders: (arg: ReadonlyArray<CalendarEventVersionReminder>) => void,
) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="subscribeSmall" color={theme.greys.shade70} />}
          content={<CalendarRemindersEditor reminders={reminders} onRemindersChange={setReminders} />}
        />
      )}
    </UserThemeConsumer>
  );
}

export function renderMoreOptionsSection(
  selectedCalendarData: SelectedCalendarData,
  showAs: CalendarEventVersionShowAsTypesEnum,
  onShowAsChange: (arg: CalendarEventVersionShowAsTypesEnum) => void,
  visibility: CalendarEventVersionVisibilityTypesEnum,
  setVisibility: (arg: CalendarEventVersionVisibilityTypesEnum) => void,
) {
  const {calendar, calendarVersion} = selectedCalendarData;
  // Outlook does not support creating private from non-owner calendars.
  const calendarSupportsPrivateEvents =
    calendarVersion.accessRole === CalendarVersionAccessRolesEnum.OWNER ||
    calendar.behaviors.writerCanEditPrivateEvents;

  const shouldHidePublicVisibility = calendar.provider === CalendarProviderTypesEnum.OFFICE365;

  return (
    <UserThemeConsumer>
      {(theme) => (
        <CalendarEventDetailRow
          iconContent={<StyledSectionIcon name="eyeSmall" color={theme.greys.shade70} />}
          content={
            <StyledMoreOptionsSectionWrapperDiv>
              <StyledCalendarShowAsDropdownBox showAs={showAs} onShowAsChange={onShowAsChange} />
              <StyledCalendarVisibilityDropdownBox
                visibility={visibility}
                onVisibilityChange={setVisibility}
                shouldDisablePrivateVisibility={!calendarSupportsPrivateEvents}
                shouldHidePublicVisibility={shouldHidePublicVisibility}
              />
            </StyledMoreOptionsSectionWrapperDiv>
          }
        />
      )}
    </UserThemeConsumer>
  );
}

export function maybeRenderSaveWarning(
  shouldShowSaveWarning: boolean,
  setSaveWarning: (val: boolean) => void,
  onSaveClick: (options: SaveOptions) => void,
) {
  if (!shouldShowSaveWarning) {
    return null;
  }

  return (
    <OkCancelDialog
      title={<Trans id="cal-editor-are-you-sure">Are you sure?</Trans>}
      onOkClick={() => onSaveClick({hasConfirmedSave: true})}
      onRequestClose={() => setSaveWarning(false)}
    >
      <Trans id="cal-editor-organizer-warning">
        These changes will only be reflected on your calendar because you&apos;re not the event organizer.
      </Trans>
    </OkCancelDialog>
  );
}

export function maybeRenderRecurringSavePrompt(
  shouldShowRecurringSaveDialog: boolean,
  setRecurringSaveDialog: (val: boolean) => void,
  setSaveOptions: (options: SaveOptions) => void,
  onSaveClick: (options: SaveOptions) => void,
) {
  if (!shouldShowRecurringSaveDialog) {
    return null;
  }

  return (
    <RecurringUpdateDialog
      onConfirmClick={(recurringSaveType) => {
        setRecurringSaveDialog(false);
        setSaveOptions({recurringSaveType});
        onSaveClick({recurringSaveType});
      }}
      onCancelClick={() => setRecurringSaveDialog(false)}
    />
  );
}
